/**
 * KAN-746: заказы интернет-магазина: список по вкладке, счётчики вкладок,
 * карточка заказа и правки (позиции, переходы статуса, новый заказ клиента).
 *
 * Ответ правки - свежая карточка заказа: кладётся в кэш сразу, затем
 * обновляются список и счётчики заказов, карточка клиента, сметы (носитель
 * заказа) и менеджеры (суммы заказов).
 * KAN-759: и доска CRM (столбцы "Заказы" и "Текущий заказ").
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { ordersApi } from '@/services/api/orders';
import { CRM_BOARD_QUERY_KEY, MANAGERS_QUERY_KEY, ORDERS_QUERY_KEY } from '@/services/api/queryKeys';
import { clientQueryKey } from '@/services/api/clientQueries';
import type { OrderAction, OrderDetail, OrderItemsPatch, OrdersListParams } from '@/types/order';

export const ordersListKey = (params: OrdersListParams) =>
  [...ORDERS_QUERY_KEY, 'list', params] as const;
export const orderCountsKey = [...ORDERS_QUERY_KEY, 'counts'] as const;
export const orderDetailKey = (id: string | undefined) =>
  [...ORDERS_QUERY_KEY, 'detail', id] as const;

export function useOrdersList(params: OrdersListParams) {
  return useQuery({
    queryKey: ordersListKey(params),
    queryFn: () => ordersApi.list(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useOrderCounts() {
  return useQuery({
    queryKey: orderCountsKey,
    queryFn: ordersApi.counts,
    retry: false,
  });
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: orderDetailKey(id),
    queryFn: () => ordersApi.get(id as string),
    enabled: !!id,
    retry: false,
  });
}

export const CLIENT_RECENT_ORDERS_LIMIT = 5;

/**
 * Последние заказы клиента по всем статусам одним запросом (tab=all: без
 * пустых черновиков), свежие сверху; count - всего заказов клиента.
 */
export function useClientRecentOrders(clientId: string) {
  const params: OrdersListParams = { tab: 'all', client: clientId, page: 1, page_size: CLIENT_RECENT_ORDERS_LIMIT };
  return useQuery({
    queryKey: ordersListKey(params),
    queryFn: () => ordersApi.list(params),
    retry: false,
  });
}

/** Обновить всё, что показывает заказ, после его изменения. */
export function refreshAfterOrderChange(client: QueryClient, order: OrderDetail): void {
  client.setQueryData(orderDetailKey(order.id), order);
  const keys: ReadonlyArray<readonly unknown[]> = [
    ORDERS_QUERY_KEY,
    ['estimate', order.id],
    ['estimates'],
    ['clients'],
    MANAGERS_QUERY_KEY,
    CRM_BOARD_QUERY_KEY,
  ];
  for (const queryKey of keys) void client.invalidateQueries({ queryKey });
  if (order.client) {
    void client.invalidateQueries({ queryKey: clientQueryKey(order.client.id) });
  }
}

/** Добавить товар, изменить количество, удалить позицию. */
export function useOrderItemsMutation(orderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: OrderItemsPatch) => ordersApi.patchItems(orderId, patch),
    onSuccess: (order) => refreshAfterOrderChange(client, order),
  });
}

/** Оформить, подтвердить, отменить. */
export function useOrderTransition(orderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (action: OrderAction) => ordersApi.transition(orderId, action),
    onSuccess: (order) => refreshAfterOrderChange(client, order),
  });
}

/** KAN-755: применить промокод (code) или снять его (null). */
export function useOrderPromoMutation(orderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string | null) =>
      code === null ? ordersApi.removePromo(orderId) : ordersApi.applyPromo(orderId, code),
    onSuccess: (order) => refreshAfterOrderChange(client, order),
  });
}

/** Новый заказ из карточки клиента (или его текущий черновик). */
export function useCreateOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => ordersApi.create(clientId),
    onSuccess: (order) => refreshAfterOrderChange(client, order),
  });
}
